import React, { useEffect, useState, useRef } from "react";
import {
  Row,
  Col,
  Form,
  Button,
  Table,
} from "react-bootstrap";
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';

import { getFromLS, setToLS } from "../../utils/storage";
import styles from "./tieout.module.css";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { FaTrashAlt, FaDownload, FaCloudDownloadAlt } from 'react-icons/fa';
import { ThreeCircles } from "react-loader-spinner";

dayjs.extend(utc);
dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_INTERNAL_K1_BASE_URL;
const API_URL2 = 'http://127.0.0.1:5000';

interface PartnershipInfo {
  name: string;
  ein: string;
  returnId: string;
}

interface Partner {
  id: string;
  name: string;
  ssn: string;
  entity_type: string;
  return_id_options: string[];
  selected: boolean;
  selectedReturn: string;
  selectedOutputVersion?: string;
  status: string;
  statusMessage?: string;
}
interface FlowHistoryItem {
    PartnerName: string;
    Message: string;
    FileURL: string;
    UpdatedDate: string;
    NumberOfPartnerships: number;
}

interface FlowHistoryResponse {
    success: boolean;
    data: FlowHistoryItem[];
}

interface ChildModeState {
    ein: string;
    availableReturns: string[];
    selectedReturn: string;
    partnershipInfo: PartnershipInfo;
    partners: Partner[];
}

interface ParentModeState {
    ein: string;
    partnershipInfo: PartnershipInfo;
    partners: Partner[];
    childReturnVersion: string;
    childOutputVersion: string;
    childReturnOptions: string[];
}

/**
 * internalK1 Parent Component
 * This component serves as the main container and can have child components inside "components/" folder.
 */
const Tieout: React.FC = () => {
    // Common state
    const [status, setStatus] = useState<string[]>([]);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [batchId, setBatchId] = useState<string | null>(null);
    const [isLoadingReturns, setIsLoadingReturns] = useState<boolean>(false);
    const [history, setHistory] = useState<FlowHistoryItem[]>([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
    const [isFetchParent, setIsFetchParent] = useState<boolean>(false);

    // Child mode state
    const [childModeState, setChildModeState] = useState<ChildModeState>({
        ein: '',
        availableReturns: [],
        selectedReturn: '',
        partnershipInfo: { name: "", ein: "", returnId: "" },
        partners: []
    });

    // Parent mode state
    const [parentModeState, setParentModeState] = useState<ParentModeState>({
        ein: '',
        partnershipInfo: { name: "", ein: "", returnId: "" },
        partners: [],
        childReturnVersion: '',
        childOutputVersion: 'Same',
        childReturnOptions: []
    });

    // Helper function to get current mode's state
    const getCurrentState = () =>  childModeState;

    const token: string = getFromLS("token") ?? "";

    // Helper function to update current mode's state
    const updateCurrentState = (updates: Partial<ChildModeState | ParentModeState>) => {
        if (isFetchParent) {
            setParentModeState(prev => ({ ...prev, ...updates }));
        } else {
            setChildModeState(prev => ({ ...prev, ...updates }));
        }
    };

    // Update the switch handler to preserve state
    const handleModeSwitch = () => {
        setIsProcessing(false);
        setBatchId(null);
        setStatus([]);
        setIsFetchParent(!isFetchParent);
    };

    // Update all the existing state references to use the new state management
    const ein = getCurrentState().ein;
    const setEin = (value: string) => updateCurrentState({ ein: value });


    const selectedReturn = isFetchParent ? '' : childModeState.selectedReturn;
    const setSelectedReturn = (value: string) => updateCurrentState({ selectedReturn: value });

    const partnershipInfo = getCurrentState().partnershipInfo;
    const setPartnershipInfo = (value: PartnershipInfo) => updateCurrentState({ partnershipInfo: value });


    const [partners, setPartners] = useState<Partner[]>([]);

    const [childReturnVersion, setChildReturnVersion] = useState<string>('');

    const childOutputVersion = parentModeState.childOutputVersion;
    const setChildOutputVersion = (value: string) => updateCurrentState({ childOutputVersion: value });

    const [childReturnOptions, setChildReturnOptions] = useState([]);


    // things for loader
    const [isParentInfoLoading, setIsParentInfoLoading] = useState(false);
    const [generateTieoutLoading, setGenerateTieoutLoading] = useState(false);
    const [refreshHistoryLoading, setRefreshHistoryLoading] = useState(false);


    const gridRef = useRef<AgGridReact>(null);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [gridColumnApi, setGridColumnApi] = useState<any>(null);
    const statusBoxRef = useRef<HTMLDivElement>(null);

   

    const gridStyle = {
        width: '100%',
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        height: '100%'
    };

    const columnDefs: ColDef[] = [
        {
            headerName: '',
            field: 'selected',
            checkboxSelection: true,
            headerCheckboxSelection: true,
            width: 100,
            pinned: 'left' as const,
            cellRenderer: () => '',
            resizable: false,
        },
        { headerName: 'Name', field: 'name', flex: 2 },
        { headerName: 'SSN/EIN', field: 'ssn', flex: 1 },
        { headerName: 'Entity Type', field: 'entity_type', flex: 1 },
        {
            headerName: 'Return ID',
            field: 'selectedReturn',
            flex: 2,
            cellRenderer: (params: any) => (
                <Form.Select
                    value={params.data.selectedReturn}
                    onChange={(e) => handleSelectChange(params.rowIndex, e.target.value)}
                    disabled={isProcessing}
                    className={styles.formSelect}
                >
                    {params.data.return_id_options.map((option: string, idx: number) => (
                        <option key={idx} value={option}>{option}</option>
                    ))}
                </Form.Select>
            )
        }
    ];

    const historyColumnDef: ColDef[] = [
        {
            headerName: 'Generated At',
            field: 'UpdatedDate',
            valueFormatter: ({ value }) => formatTimestamp(value),
            flex: 1,
        },
        { headerName: 'Partner', field: 'PartnerName', flex: 1 },
        { headerName: 'Number Of Partnerships', field: 'NumberOfPartnerships', flex: 1 },
        { headerName: 'Message', field: 'Message', flex: 2 },
        {
            headerName: 'Action',
            field: 'FileURL',
            flex: 1,
            cellRenderer: (params: any) => (
            <Button
                variant="primary"
                disabled={!params.value}
                onClick={() => handleDownload(params.value)}
                className={styles.primaryButton}
            >
                Download <FaCloudDownloadAlt />
            </Button>
            ),
        },
        ]

    const defaultColDef: ColDef = {
        sortable: true,
        filter: true,
        resizable: true,
    };

    const onGridReady = (params: GridReadyEvent) => {
        setGridApi(params.api);
        // setGridColumnApi(params.columnApi);
        params.api.sizeColumnsToFit();
    };

    const onFirstDataRendered = (params: any) => {
        params.api.sizeColumnsToFit();
    };

    useEffect(() => {
        setToLS("url", ["myflows"])
    }, []);

    // handle download
    const handleDownload = async (url: string) => {
        try {
            url = `${API_URL}/api/download?file_name=${url}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                cache: 'no-store',
                mode: 'cors'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `tieout_${Date.now()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        }
        catch (error) {
            console.error('Error downloading file:', error);
            addStatusMessage(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    // Handle the flow data action
    const handleFlowData = async () => {
        setGenerateTieoutLoading(true);
        const selectedRows = partners.filter(row => row.selected);
        if (selectedRows.length === 0) {
            addStatusMessage("Please select at least one entity to flow data to.");
            return;
        }

        if (isFetchParent && !childReturnVersion) {
            addStatusMessage("Please select a child return version.");
            return;
        }

        setIsProcessing(true);
        addStatusMessage("Starting data flow process...");

        try {
            // alert(childReturnVersion);
            // return
            
                // Pull mode - using new APIs
                const selectedParents = selectedRows.map(row => row.selectedReturn);
                const outputVersion = childOutputVersion === "Same" ? "" : childOutputVersion;

                const requestData = {
                    PartnerName: partnershipInfo.name,
                    ChildReturnID: childReturnVersion,
                    SelectedParents: selectedParents,
                    OutputVersion: outputVersion
                };

                console.log('Submitting pull batch with data:', requestData);

                console.log('Request data:', requestData);

                const response = await fetch(`${API_URL}/api/tieout`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(requestData)
                });
                fetchFlowHistory();
                setGenerateTieoutLoading(false);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const result = await response.json();
                console.log('Pull batch submission response:', result);
                
                if (result.status === 'success') {
                    setBatchId(result.batch_id);
                    addStatusMessage("Pull batch submitted successfully. Processing started...");
                    
                    setPartners(prevData => 
                        prevData.map(row => ({
                            ...row,
                            status: row.selected ? 'processing' : row.status
                        }))
                    );

                    
                } else {
                    throw new Error(result.message || 'Failed to submit pull batch');
                }
            
        } catch (error) {
            console.error('Error submitting batch:', error);
            addStatusMessage(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
            setIsProcessing(false);
        }
    };

    const refreshHistory = async () => {
        setRefreshHistoryLoading(true);
        await fetchFlowHistory();
        setRefreshHistoryLoading(false);
    }
    


    // Helper function to append a status message
    const addStatusMessage = (message: string) => {
        setStatus(prevMessages => [...prevMessages, message]);
    };

 
    // Fetch table data for the selected return
    const handleGetTableData = async () => {
        

        setIsParentInfoLoading(true);
        
        setPartners([]);
        
        try {
            console.log(token);
            console.log('Fetching table data for return ID:', selectedReturn);
            
            // Add retry logic for the table data request
            let retries = 3;
            let tableResponse: Response | undefined;
            let responseText: string | null = null;
            
            while (retries > 0) {
                
                try {
                    const url = `${API_URL}/api/get_pull_table_data?child_ssn=${encodeURIComponent(ein)}`
                        
                    tableResponse = await fetch(url, {
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        credentials: 'include',
                        cache: 'no-store',
                        mode: 'cors'
                    });
                    // console.log(table)
                    if (tableResponse.ok) break;
                    
                    // Store the response text so we don't try to read it again
                    responseText = await tableResponse.text();
                    console.error(`Attempt ${4-retries} failed:`, responseText);
                    retries--;
                    
                    if (retries > 0) {
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    }
                } catch (error) {
                    console.error(`Attempt ${4-retries} failed:`, error);
                    retries--;
                    if (retries === 0) throw error;
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }
            setIsParentInfoLoading(false);
            if (!tableResponse) {
                throw new Error('Failed to get response after retries');
            }

            if (!tableResponse.ok) {
                // Use the stored response text if available, otherwise read it again
                const errorText = responseText || await tableResponse.text();
                throw new Error(`Failed to fetch table data after retries: ${errorText}`);
            }

            const data = await tableResponse.json();
            console.log('Table data response:', data);

            // Handle different response formats based on mode
            
                // Handle parent mode response
                if (data.status === 'failure') {
                    throw new Error(data.message || 'Failed to load child data');
                }

                if (!data.ChildClientID || !data.ChildName || !data.ChildSSN) {
                    throw new Error('Invalid response: Missing child information');
                }

                setPartnershipInfo({
                    name: data.ChildName,
                    ein: data.ChildSSN,
                    returnId: data.ChildClientID
                });

                // Set child return options from the child's return options
                if (data.ChildReturnOptions && Array.isArray(data.ChildReturnOptions) && data.ChildReturnOptions.length > 0) {
                    console.log('Child return options:', data.ChildReturnOptions);
                    setChildReturnOptions(data.ChildReturnOptions);
                    setChildReturnVersion(data.ChildReturnOptions[0]);
                    // setChildReturnVersion(data.ChildReturnOptions[0]);
                } else {
                    throw new Error('Invalid response: ChildReturnOptions is missing or empty');
                }

                if (!data.TableData || !Array.isArray(data.TableData)) {
                    throw new Error('Invalid response: TableData is missing or not an array');
                }

                const modifiedData = data.TableData.map((row: any) => ({
                    ...row,
                    selected: false,
                    selectedReturn: row.return_id_options[0],
                    status: 'Idle',
                    ssn: row.ein
                }));
                
                setPartners(modifiedData);
             

            addStatusMessage("Partnership data loaded successfully.");
        } catch (error) {
            console.error('Detailed error:', error);
            let errorMessage = error instanceof Error ? error.message : 'Unknown error';
            if (errorMessage.includes('<!doctype')) {
                errorMessage = 'Server error: Failed to process the request. Please try again or contact support if the issue persists.';
            }
            addStatusMessage(`Error: ${errorMessage}`);
            setPartners([]);
            setPartnershipInfo({
                name: "",
                ein: ein,
                returnId: ""
            });
        }
    };

    // Update the selected dropdown value for a given row
    const handleSelectChange = (index: number, value: string) => {
        setPartners((prevData: Partner[]) => {
            const newData = [...prevData];
            newData[index].selectedReturn = value;
            return newData;
        });
    };

    // Update the output version for a given row
    const handleOutputVersionChange = (index: number, value: string) => {
        setPartners((prevData: Partner[]) => {
            const newData = [...prevData];
            newData[index].selectedOutputVersion = value;
            return newData;
        });
    };

    // Fetch flow history
    const fetchFlowHistory = async () => {
        setIsLoadingHistory(true);
        try {
            const params = new URLSearchParams();
            if (ein) params.append('partnership_ein', ein);
            params.append('max_results', '10'); // Default to 10 results

            console.log('Fetching flow history with params:', params.toString());

            const response = await fetch(`${API_URL}/api/tieout`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                cache: 'no-store',
                mode: 'cors'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data: FlowHistoryResponse = await response.json();
            console.log('Flow history response:', data);
            
            if (data.success ) {
                setHistory(data.data);
            } else {
                throw new Error('Failed to fetch history');
            }
        } catch (error) {
            console.error('Error fetching history:', error);
            addStatusMessage(`Error fetching history: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    // Format timestamp for display
    const formatTimestamp = (timestamp: string) => {
        console.log('Original timestamp:', timestamp);
        // Parse the ISO timestamp and convert to local time
        const localTime = dayjs(timestamp).local();
        console.log('Local time:', localTime.format());
        console.log('Current timezone:', dayjs.tz.guess());
        console.log('Current local time:', dayjs().format());
        
        return localTime.format('MM/DD/YYYY hh:mm:ss A');
    };

    // Add a function to clear the current mode's state
    const handleClearPage = () => {
        
            // Clear parent mode state
            setParentModeState({
                ein: '',
                partnershipInfo: { name: "", ein: "", returnId: "" },
                partners: [],
                childReturnVersion: '',
                childOutputVersion: 'Same',
                childReturnOptions: []
            });
        
        
        // Clear common state
        setStatus([]);
        setIsProcessing(false);
        setBatchId(null);
    };

    return (
        <div className={styles.container}>
            <div className={styles.headerContainer}>
                <h3 className={styles.heading}>Cygnus K1 Tieout Utility</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    
                    <Button 
                        variant="outline-danger" 
                        size="sm" 
                        onClick={handleClearPage}
                        title="Clear current page"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                        <FaTrashAlt /> Clear
                    </Button>
                </div>
            </div>
            
            <div className={`${styles.topSection}`}>
                <div className={`${styles.leftSection} `}>
                        <Row >
                            <Col md={4} className="d-flex flex-column">

                                <Row>
                                    <Col md={6}>
                                    <Form.Group >
                                            <Form.Label>Enter EIN/SSN of Partner</Form.Label>
                                            <Form.Control 
                                            type="text" 
                                            placeholder=""
                                            value={ein}
                                            onChange={(e) => setEin(e.target.value)}
                                            disabled={isProcessing || isLoadingReturns}
                                            className={styles.formControl}
                                        />
                                    </Form.Group>
                                        
                                    <div className="d-flex align-items-center gap-2 mt-2">
                                        <Button 
                                            variant="primary" 
                                            onClick={handleGetTableData}
                                            disabled={isProcessing || !ein}
                                            className={styles.customButton}
                                        >
                                            Fetch Partner Details
                                        </Button>
                                            {
                                                isParentInfoLoading && 
                                                <ThreeCircles
                                                    height="25"
                                                    width="25"
                                                    color="#4fa94d"
                                                    visible={true}
                                                    ariaLabel="three-circles-rotating"
                                                    outerCircleColor="var(--theme1_primary)"
                                                    innerCircleColor="var(--theme1_secondary)"
                                                    middleCircleColor="var(--theme1_tertiary)"
                                                />
                                            }
                                        
                                        </div>
                                        
                                    </Col>

                                    <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Partner Return Version</Form.Label>
                                        <Form.Select
                                            value={childReturnVersion}
                                            onChange={(e) => setChildReturnVersion(e.target.value)}
                                            disabled={isProcessing || ein === "" || childReturnOptions.length === 0}
                                            className={styles.formSelect}
                                        >
                                            {childReturnOptions.map((option, index) => (
                                                <option key={index} value={option}>{option}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                           
                                    </Col>

                                </Row>

                                

                                    <div className={styles.infoBox}>
                                        <p data-label="Partner Name">{partnershipInfo.name || '-'}</p>
                                        <p data-label="EIN:">{partnershipInfo.ein || '-'}</p>
                                        <p data-label="Return ID:">{partnershipInfo.returnId || '-'}</p>
                                    </div>
                            </Col>

                            <Col md={8} className="d-flex flex-column">
                                <div className="ag-theme-balham" style={gridStyle}>
                                   { 
                                        partners.length !== 0 ?
                                        <div>
                                        <h5 className={styles.compactHeaderPartnerships} >Available Partnerships of {partnershipInfo.name}</h5>
                                            <p className={styles.paernershipsInformationText} >Please select the ones which you want to tieout with.</p>
                                        </div>
                                        :
                                        <div>
                                        <h5 className={styles.compactHeaderPartnerships}>Partnerships</h5>
                                        <p className={styles.paernershipsInformationText} > &nbsp; </p>
                                        </div>
                                   } 
                                    <AgGridReact
                                        ref={gridRef}
                                        rowData={partners}
                                        columnDefs={columnDefs}
                                        defaultColDef={defaultColDef}
                                        onGridReady={onGridReady}
                                        alwaysShowHorizontalScroll={false}
                                        alwaysShowVerticalScroll={false}
                                        rowHeight={50}
                                        headerHeight={38}
                                        suppressContextMenu={true}
                                        onFirstDataRendered={onFirstDataRendered}
                                        domLayout="autoHeight"
                                        getRowHeight={() => 50}
                                        rowSelection="multiple"
                                        suppressRowClickSelection={true}
                                        onSelectionChanged={(event) => {
                                            const selectedNodes = event.api.getSelectedNodes();
                                            const newData = [...partners];
                                            newData.forEach(row => {
                                                row.selected = selectedNodes.some(node => node.data.id === row.id);
                                            });
                                            setPartners(newData);
                                        }}
                                        isRowSelectable={(params) => !isProcessing}
                                    />
                                </div>
                            </Col> 
                        </Row>                    
                </div>

                
            </div>

            <div className={`container-fluid`}>

                <Row>
                    <Col md={2}>
                        <div className="d-flex align-items-center gap-2 mt-2">
                            <Button
                                variant="secondary"
                                onClick={handleFlowData}
                                disabled={isProcessing || !partners.some(row => row.selected)}
                                className={styles.secondaryButton}
                            >
                                Generate Tieout
                            </Button>
                            {
                                generateTieoutLoading &&
                                <ThreeCircles
                                    height="25"
                                    width="25"
                                    color="#4fa94d"
                                    visible={true}
                                    ariaLabel="three-circles-rotating"
                                    outerCircleColor="var(--theme1_primary)"
                                    innerCircleColor="var(--theme1_secondary)"
                                    middleCircleColor="var(--theme1_tertiary)"
                                />
                            }
                        </div>
                    </Col>
                    <Col md={8}>
                    </Col>
                    <Col md={2}>
                        <div className="d-flex align-items-center gap-2 mt-2" style={{ justifyContent: 'flex-end' }}>
                            {
                                refreshHistoryLoading && 
                                <ThreeCircles
                                        height="25"
                                        width="25"
                                        color="#4fa94d"
                                        visible={true}
                                        ariaLabel="three-circles-rotating"
                                        outerCircleColor="var(--theme1_primary)"
                                        innerCircleColor="var(--theme1_secondary)"
                                        middleCircleColor="var(--theme1_tertiary)"
                                    />
                            }
                                <Button
                                            variant="secondary"
                                            onClick={refreshHistory}
                                            // disabled={isProcessing || !partners.some(row => row.selected)}
                                            className={styles.secondaryButton}
                                        >
                                            Refresh History
                                </Button>
                        </div>
                    </Col>
                </Row>
            </div>
            <div className={`container-fluid ${styles.historyTableContainer}`}>
            <div className={`ag-theme-balham ${styles.historyTableGridTieout} debug`} >
                {/* <h6>History</h6> */}
                    <AgGridReact
                        rowData={history}
                        columnDefs={historyColumnDef}
                        defaultColDef={defaultColDef}
                        onGridReady={onGridReady}
                        alwaysShowHorizontalScroll={false}
                        alwaysShowVerticalScroll={false}
                        rowHeight={50}
                        headerHeight={38}
                        suppressContextMenu={true}
                        onFirstDataRendered={onFirstDataRendered}
                        domLayout="autoHeight"
                        getRowHeight={() => 50}
                    />
                </div>
            </div>

           
        </div>
    );
}

export default Tieout;
